import { createAction } from 'actions/utils'
import * as applicationActions from 'actions/application.js'
import { moduleArraySchema, vehicleModulesSchema } from 'schemas/ecus'
import { vehicles, searchDeprecated, vehicleComments } from 'services'
import {
  ptpErrorHandler,
  rpSaveErrorHandler,
  createModalErrorHandler,
  mutationErrorHandler,
} from 'sagas/errorHandlers'

// Per-call counter for updateVehicleFeatures de-dup IDs.
// A counter is deterministic and avoids the rare same-millisecond collision
// that Date.now() could produce when two saves fire in the same ms.
let updateFeaturesCallCount = 0

// Per-call counter for validateVehicleFeatures de-dup IDs.
// Same rationale as updateFeaturesCallCount above — Date.now() has millisecond
// resolution and two concurrent validation calls (e.g., conflict check + switch
// panel check) dispatched in the same ms would get the same id and be de-duped.
let validateFeaturesCallCount = 0

/**
 * Reset the module-level call counters back to zero.
 *
 * Jest does NOT reload modules between tests by default, so these counters
 * accumulate across tests in the same suite. Call this in a `beforeEach` in
 * any test file that exercises `updateVehicleFeatures` or `validateVehicleFeatures`
 * to guarantee a deterministic id for every test.
 *
 * HMR note: in development, Webpack HMR does not re-execute module-level code,
 * so these counters also persist across hot reloads. After an HMR cycle, IDs
 * start from N instead of 1 — this is intentional. The only observable effect
 * is that de-dup IDs in Redux DevTools are not sequential after a reload, which
 * is a cosmetic dev-only inconvenience with no production impact.
 *
 * @example
 *   import { resetCallCounters } from 'actions/truck'
 *   beforeEach(() => { resetCallCounters() })
 */
export const resetCallCounters = () => {
  updateFeaturesCallCount = 0
  validateFeaturesCallCount = 0
}

export const HAS_SERVICE_RECORD_DATE = 'HAS_SERVICE_RECORD_DATE'
export const SET_SERVICE_RECORD_MODAL = 'SET_SERVICE_RECORD_MODAL'

export const TOGGLE_SELECTED_TRUCK = 'TOGGLE_SELECTED_TRUCK'
export const toggleSelectedTruck = (serialNumber) => ({
  serialNumber,
  type: TOGGLE_SELECTED_TRUCK,
})

export const CLEAR_SELECTED_TRUCKS = 'CLEAR_SELECTED_TRUCKS'
export const clearSelectedTrucks = () => ({
  type: CLEAR_SELECTED_TRUCKS,
})

export const REVERT_ALL_COMPARISON_CHANGES = 'REVERT_ALL_COMPARISON_CHANGES'
export const revertAllChanges = () => ({
  type: REVERT_ALL_COMPARISON_CHANGES,
})

export const STAGE_REFERENCE_PARAMETER_ON_VEHICLE =
  'STAGE_REFERENCE_PARAMETER_ON_VEHICLE'
export const SHOW_RP_PIN_ASSIGNMENT_MODAL = 'SHOW_RP_PIN_ASSIGNMENT_MODAL'
export const HIDE_RP_PIN_ASSIGNMENT_MODAL = 'HIDE_RP_PIN_ASSIGNMENT_MODAL'

export const showRPPinAssignmentModal = (serialNumber) => (dispatch) => {
  dispatch({
    type: SHOW_RP_PIN_ASSIGNMENT_MODAL,
    serialNumber,
  })
}

export const CLEAR_STAGED_REFERENCE_PARAMETERS_ON_VEHICLE =
  'CLEAR_STAGED_REFERENCE_PARAMETERS_ON_VEHICLE'
export const clearStagedRPsOnVehicle = (serialNumber) => ({
  serialNumber,
  type: CLEAR_STAGED_REFERENCE_PARAMETERS_ON_VEHICLE,
})

export const REVERT_VEHICLE_CHANGES = 'REVERT_VEHICLE_CHANGES'
export const revertVehicleChanges = (serialNumber) => ({
  serialNumber,
  type: REVERT_VEHICLE_CHANGES,
})

export const REQUESTING_TRUCK_DETAILS = 'REQUESTING_TRUCK_DETAILS'
export const TRUCK_DETAILS_RECEIVED = 'TRUCK_DETAILS_RECEIVED'
export const TRUCK_DETAILS_NOT_RECEIVED = 'TRUCK_DETAILS_NOT_RECEIVED'

export const GET_TRUCK_DETAILS = 'GET_TRUCK_DETAILS'
export const getTruckDetails = ({ serialNumber }) =>
  applicationActions.apiAction({
    asyncFn: vehicles.getDetails,
    args: { serialNumber },
    successActionType: GET_TRUCK_DETAILS,
    requestActionType: REQUESTING_TRUCK_DETAILS,
    failureActionType: TRUCK_DETAILS_NOT_RECEIVED,
    id: serialNumber,
  })
export const getTruckDetailsStub = ({ id }) =>
  applicationActions.apiActionStub({
    asyncFn: vehicles.getDetails,
    successActionType: GET_TRUCK_DETAILS,
    id,
  })

export const GET_VALIDATE_PTP = 'GET_VALIDATE_PTP'
export const VALIDATE_PTP_FAILED = 'VALIDATE_PTP_FAILED'
export const getValidatePtp = (serialNumber) =>
  applicationActions.apiAction({
    asyncFn: vehicles.getValidatePtp,
    args: { serialNumber },
    successActionType: GET_VALIDATE_PTP,
    failureActionType: VALIDATE_PTP_FAILED,
    id: serialNumber,
    // ptpErrorHandler: handles PtpError thrown by the service layer when the
    //   backend returns HTTP 200 with result.code === '424'. Shows the PTP-specific
    //   modal copy (see services/vehicles.getValidatePtp and ptpErrorHandler.js).
    //
    // mutationErrorHandler: fallback for plain HTTP failures (network errors, 5xx,
    //   timeouts) that ptpErrorHandler skips because they are not PtpError instances.
    //   Without this, defaultErrorHandler's GET-is-silent rule would swallow them
    //   and the user would receive no feedback about the failed validation.
    //   This restores the pre-migration behavior where any VALIDATE_PTP_FAILED
    //   dispatch caused a modal to be shown (see old errorModals.js).
    errorHandlers: [ptpErrorHandler, mutationErrorHandler],
  })

export const GET_VEHICLE_COMMENTS = 'GET_VEHICLE_COMMENTS'
export const GET_VEHICLE_COMMENTS_RECEIVED = 'GET_VEHICLE_COMMENTS_RECEIVED'

export const getVehicleComments = ({ serialNumber }) =>
  applicationActions.apiAction({
    successActionType: GET_VEHICLE_COMMENTS_RECEIVED,
    asyncFn: vehicleComments.getVehicleComments,
    args: { serialNumber },
    id: serialNumber,
  })

export const ADD_VEHICLE_COMMENT = 'ADD_VEHICLE_COMMENT'
export const ADD_VEHICLE_COMMENT_SUCCESS = 'ADD_VEHICLE_COMMENT_SUCCESS'

export const addVehicleComment = ({ serialNumber, comment }) =>
  applicationActions.apiAction({
    successActionType: ADD_VEHICLE_COMMENT_SUCCESS,
    asyncFn: vehicleComments.addVehicleComment,
    args: { serialNumber, comment },
    id: serialNumber,
  })

export const SET_VEHICLE_TEMP_COMMENT = 'SET_VEHICLE_TEMP_COMMENT'

export const setVehicleTempComment = ({ serialNumber, commentString }) => ({
  type: SET_VEHICLE_TEMP_COMMENT,
  serialNumber,
  commentString,
})

export const GET_VEHICLE_REFERENCE_PARAMETERS =
  'GET_VEHICLE_REFERENCE_PARAMETERS'
export const GET_VEHICLE_REFERENCE_PARAMETERS_FAILURE =
  'GET_VEHICLE_REFERENCE_PARAMETERS_FAILURE'
// Re-export for the reducer handler map — this action type is dispatched by
// apiCall when getVehicleReferenceParameters fires, so the reducer can set
// isLoading: true while the request is in-flight.
export const REQUESTING_TRUCK_REFERENCE_PARAMETERS =
  'REQUESTING_TRUCK_REFERENCE_PARAMETERS'
export const getVehicleReferenceParameters = ({ serialNumber }) =>
  applicationActions.apiAction({
    successActionType: GET_VEHICLE_REFERENCE_PARAMETERS,
    asyncFn: searchDeprecated.referenceParameters,
    args: { serialNumber },
    id: serialNumber,
    // requestActionType sets isLoading: true in the truck reducer while the
    // request is in-flight. Without it the old REQUESTING_TRUCK_REFERENCE_PARAMETERS
    // handler (which sets isLoading: true) is never triggered.
    requestActionType: REQUESTING_TRUCK_REFERENCE_PARAMETERS,
    // failureActionType clears isLoading and sets error state in the truck
    // reducer so the M2 vehicle summary page doesn't show a permanent spinner
    // on fetch failure.
    failureActionType: GET_VEHICLE_REFERENCE_PARAMETERS_FAILURE,
  })
export const getVehicleReferenceParametersStub = ({ id }) =>
  applicationActions.apiActionStub({
    successActionType: GET_VEHICLE_REFERENCE_PARAMETERS,
    asyncFn: searchDeprecated.referenceParameters,
    id,
  })

export const VEHICLE_FEATURES_RECEIVED = 'VEHICLE_FEATURES_RECEIVED'
export const requestVehicleFeatures = (serialNumber) =>
  applicationActions.apiAction({
    successActionType: VEHICLE_FEATURES_RECEIVED,
    asyncFn: vehicles.getFeatures,
    args: { serialNumber },
    id: serialNumber,
  })

export const TRUCK_FEATURES_UPDATED = 'TRUCK_FEATURES_UPDATED'
export function truckFeaturesUpdated(serialNumber, response) {
  const { data } = response

  return {
    serialNumber,
    data,
    type: TRUCK_FEATURES_UPDATED,
  }
}

export const VEHICLE_FEATURES_UPDATE_FAILURE = 'VEHICLE_FEATURES_UPDATE_FAILURE'

/**
 * Save added/removed/updated features and switches for a vehicle.
 * Uses promiseApiAction so callers can await the result.
 *
 * @param {string} serialNumber
 * @param {object} body - { addedFeatures, removedFeatures, notes, addedSwitches?, removedSwitches?, addedSolenoids? }
 */
export const updateVehicleFeatures = (serialNumber, body) => {
  updateFeaturesCallCount += 1
  return applicationActions.promiseApiAction({
    asyncFn: vehicles.updateVehicleFeatures,
    args: { serialNumber, body },
    successActionType: TRUCK_FEATURES_UPDATED,
    failureActionType: VEHICLE_FEATURES_UPDATE_FAILURE,
    // Use a per-call counter so that two rapid saves to the same vehicle are
    // never silently de-duplicated. A counter is deterministic and avoids the
    // same-millisecond collision that Date.now() could produce.
    id: `${serialNumber}-${updateFeaturesCallCount}`,
  })
}

export const VALIDATE_VEHICLE_FEATURES_SUCCESS =
  'VALIDATE_VEHICLE_FEATURES_SUCCESS'
export const VALIDATE_VEHICLE_FEATURES_FAILURE =
  'VALIDATE_VEHICLE_FEATURES_FAILURE'

/**
 * Validate features against a vehicle's current configuration.
 * Returns a promise that resolves with the API response data.
 * Used both for conflict detection and switch-panel pre-checks.
 *
 * @param {string} serialNumber
 * @param {object} body - Validation body (shape determines what the backend checks)
 */
export const validateVehicleFeatures = (serialNumber, body) => {
  validateFeaturesCallCount += 1
  return applicationActions.promiseApiAction({
    asyncFn: vehicles.validateVehicleFeatures,
    args: { serialNumber, body },
    successActionType: VALIDATE_VEHICLE_FEATURES_SUCCESS,
    failureActionType: VALIDATE_VEHICLE_FEATURES_FAILURE,
    // Include the body keys AND a per-call counter in the id so that concurrent
    // validation calls are not de-duplicated against each other, regardless
    // of whether the body keys or values differ. Using body keys alone (without
    // values) would incorrectly de-duplicate two calls with the same key
    // structure but different content — e.g., two addedFeatures validations
    // for different feature lists — causing the second call to resolve with
    // undefined and validateAndGetConflicts to skip the conflict check.
    //
    // A counter is used instead of Date.now() to avoid same-millisecond
    // collisions (same pattern as updateFeaturesCallCount above).
    //
    // Validation is not idempotent (different body values → different results),
    // so a per-call unique suffix is appropriate here.
    id: `${serialNumber}-${Object.keys(body || {})
      .sort()
      .join('-')}-${validateFeaturesCallCount}`,
  })
}

export const FEATURE_UPDATE_COMPLETE_BY_VEHICLE_AREA =
  'FEATURE_UPDATE_COMPLETE_BY_VEHICLE_AREA'
export const featureUpdateCompleteByVehicleArea = createAction(
  FEATURE_UPDATE_COMPLETE_BY_VEHICLE_AREA,
  ['vehicleArea', 'serialNumber', 'options']
)

export const TRUCK_MODULES_RECEIVED = 'TRUCK_MODULES_RECEIVED'
export const TRUCK_MODULES_LOADING = 'TRUCK_MODULES_LOADING'
export const TRUCK_MODULES_FAILURE = 'TRUCK_MODULES_FAILURE'
export const getTruckModules = ({ serialNumber }) =>
  applicationActions.apiAction({
    asyncFn: vehicles.getModules,
    args: { serialNumber },
    successActionType: TRUCK_MODULES_RECEIVED,
    failureActionType: TRUCK_MODULES_FAILURE,
    // Use a dedicated request action type so the reducer can set isLoading:
    // true. The generic SET_LOADING_STATUS defaults isLoading to false (the
    // reducer reads isLoading = false as the default destructure), meaning the
    // old requestActionType: SET_LOADING_STATUS never actually set a loading
    // state.
    requestActionType: TRUCK_MODULES_LOADING,
    id: serialNumber,
  })

export const TOGGLE_SELECTED_REFERENCE_PARAMETER =
  'TOGGLE_SELECTED_REFERENCE_PARAMETER'
export const toggleSelectedReferenceParameterRow = (
  serialNumber,
  referenceParameterUri
) => ({
  referenceParameterUri,
  serialNumber,
  type: TOGGLE_SELECTED_REFERENCE_PARAMETER,
})

export const TOGGLE_SELECTED_STAGED_REFERENCE_PARAMETER =
  'TOGGLE_SELECTED_STAGED_REFERENCE_PARAMETER'
export const toggleSelectedStagedReferenceParameterRow = (
  serialNumber,
  referenceParameterUri
) => ({
  referenceParameterUri,
  serialNumber,
  type: TOGGLE_SELECTED_STAGED_REFERENCE_PARAMETER,
})

export const SAVING_VEHICLE_REFERENCE_PARAMETERS =
  'SAVING_VEHICLE_REFERENCE_PARAMETERS'

export const VEHICLE_REFERENCE_PARAMETERS_SAVED =
  'VEHICLE_REFERENCE_PARAMETERS_SAVED'

export const VEHICLE_REFERENCE_PARAMETERS_SAVE_FAILED =
  'VEHICLE_REFERENCE_PARAMETERS_SAVE_FAILED'

/**
 * Save the staged reference parameters for a vehicle.
 *
 * On success, `VEHICLE_REFERENCE_PARAMETERS_SAVED` is dispatched. A dedicated
 * saga in `sagas/truck/saveReferenceParameters.js` then fires the success
 * notification and re-fetches the truck details.
 *
 * On failure the saga error handler chain runs:
 *   - PtpError (code 424 returned by the service layer) → ptpErrorHandler
 *   - All other errors → defaultErrorHandler (generic modal)
 *
 * @param {object}   vehicle         - The vehicle object (must have .serialNumber, .uri, .stagedReferenceParameters).
 * @param {boolean}  updateFleet     - Whether to propagate changes to the whole fleet.
 * @param {object[]} ctpAssignments  - CTP pin assignments to include in the request.
 * @param {string}   notes           - Change notes.
 */
export const saveVehicleReferenceParameters = (
  vehicle,
  updateFleet,
  ctpAssignments,
  notes
) => {
  const { serialNumber } = vehicle

  const ctpModels =
    ctpAssignments?.length > 0
      ? ctpAssignments
          .filter((ass) => ass.serialNumber === serialNumber)
          .map((ass) => ({
            serialNumber,
            module: ass.module,
            connector: ass.connector,
            pin: ass.pin,
            circuitNumber: ass.circuitNumber,
          }))
      : undefined

  return applicationActions.apiAction({
    asyncFn: vehicles.saveVehicleReferenceParameters,
    args: {
      serialNumber,
      vehicleUri: vehicle.uri,
      updateFleet,
      referenceParameters: vehicle.stagedReferenceParameters,
      notes,
      ...(ctpModels && { ctpModels }),
    },
    successActionType: VEHICLE_REFERENCE_PARAMETERS_SAVED,
    failureActionType: VEHICLE_REFERENCE_PARAMETERS_SAVE_FAILED,
    requestActionType: SAVING_VEHICLE_REFERENCE_PARAMETERS,
    id: serialNumber,
    errorHandlers: [ptpErrorHandler, rpSaveErrorHandler],
  })
}

export const REQUESTING_VEHICLE_FLEET_SIZE = 'REQUESTING_VEHICLE_FLEET_SIZE'
export const VEHICLE_FLEET_SIZE_RECEIVED = 'VEHICLE_FLEET_SIZE_RECEIVED'
export const VEHICLE_FLEET_NOT_RECEIVED = 'VEHICLE_FLEET_NOT_RECEIVED'
export const requestVehicleFleetSize = (serialNumber, leadSerialNumber) =>
  applicationActions.apiAction({
    asyncFn: vehicles.getFleet,
    args: { serialNumber, leadSerialNumber },
    successActionType: VEHICLE_FLEET_SIZE_RECEIVED,
    failureActionType: VEHICLE_FLEET_NOT_RECEIVED,
    requestActionType: REQUESTING_VEHICLE_FLEET_SIZE,
    id: serialNumber,
  })
export const REQUESTING_VEHICLE_REVISIONS = 'REQUESTING_VEHICLE_REVISIONS'
export const VEHICLE_REVISIONS_RECEIVED = 'VEHICLE_REVISIONS_RECEIVED'
export const VEHICLE_REVISIONS_NOT_RECEIVED = 'VEHICLE_REVISIONS_NOT_RECEIVED'
export const requestVehicleRevisions = (serialNumber) =>
  applicationActions.apiAction({
    asyncFn: vehicles.getRevisionHistory,
    args: { serialNumber },
    successActionType: VEHICLE_REVISIONS_RECEIVED,
    failureActionType: VEHICLE_REVISIONS_NOT_RECEIVED,
    requestActionType: REQUESTING_VEHICLE_REVISIONS,
    id: serialNumber,
  })

export const GET_VEHICLE_REVISIONS = 'GET_VEHICLE_REVISIONS'
export const getVehicleRevisions = ({ serialNumber }) =>
  applicationActions.apiAction({
    successActionType: GET_VEHICLE_REVISIONS,
    asyncFn: vehicles.getRevisions,
    args: { serialNumber },
    id: serialNumber,
  })
export const getVehicleRevisionsStub = ({ id }) =>
  applicationActions.apiActionStub({
    successActionType: GET_VEHICLE_REVISIONS,
    asyncFn: vehicles.getRevisions,
    id,
  })

export const ADD_EXM = 'ADD_EXM'
export const ADD_EXM_FAILURE_NOTIFY = 'ADD_EXM_FAILURE_NOTIFY'
export const addExm = ({ serialNumber, requestBody }) =>
  applicationActions.apiAction({
    asyncFn: vehicles.addExm,
    args: {
      serialNumber,
      body: requestBody,
      moduleName: requestBody.moduleName,
      size: requestBody.size,
    },
    successActionType: ADD_EXM,
    requestActionType: SET_LOADING_STATUS,
    failureActionType: ADD_EXM_FAILURE_NOTIFY,
    id: serialNumber,
    schema: moduleArraySchema,
  })

export const SET_LOADING_STATUS = 'SET_LOADING_STATUS'
export const setVehicleLoadingStatus = ({
  serialNumber,
  isLoading = false,
  errorMessage,
  actionArguments,
}) => ({
  type: SET_LOADING_STATUS,
  serialNumber: serialNumber || actionArguments.serialNumber,
  isLoading: errorMessage ? false : isLoading,
})

export const ADD_XMC = 'ADD_XMC'
export const ADD_XMC_SUCCESS_NOTIFY = 'ADD_XMC_SUCCESS_NOTIFY'
export const ADD_XMC_FAILURE_NOTIFY = 'ADD_XMC_FAILURE_NOTIFY'
export const addXmc = ({ serialNumber, requestBody }) =>
  applicationActions.apiAction({
    asyncFn: vehicles.addXmc,
    args: {
      serialNumber,
      body: {
        ...requestBody,
        modules: requestBody.modules.map((item) => item.id),
      },
      modulesWithNames: requestBody.modules,
      isLoading: true,
    },
    successActionType: ADD_XMC,
    requestActionType: SET_LOADING_STATUS,
    failureActionType: ADD_XMC_FAILURE_NOTIFY,
    id: serialNumber,
    schema: moduleArraySchema,
  })

export const UPDATE_EXM = 'UPDATE_EXM'
export const stubUpdateExm = ({ serialNumber }) =>
  applicationActions.apiActionStub({
    asyncFn: vehicles.updateExm,
    args: { serialNumber },
    successActionType: UPDATE_EXM,
    id: serialNumber,
    schema: vehicleModulesSchema,
  })

export const VEHICLE_DASH_CONFIG_CLEAR_ON_SAVE_ERROR =
  'VEHICLE_DASH_CONFIG_CLEAR_ON_SAVE_ERROR'
export const clearDashConfigOnSaveError = (serialNumber) => ({
  type: VEHICLE_DASH_CONFIG_CLEAR_ON_SAVE_ERROR,
  serialNumber,
})

export const RESET_SAVE_ERROR = 'RESET_SAVE_ERROR'
export const resetSaveError = () => (dispatch) => {
  dispatch({
    type: RESET_SAVE_ERROR,
    error: false,
  })
}

export const VALIDATE_PAR_FILE_SUCCESS = 'VALIDATE_PAR_FILE_SUCCESS'
export const VALIDATE_PAR_FILE_FAILURE = 'VALIDATE_PAR_FILE_FAILURE'
export const validateParFile = ({
  serialNumber,
  isPreProdRequest = false,
  callback,
  onError,
}) =>
  applicationActions.promiseApiAction({
    asyncFn: vehicles.validateParFile,
    // callback is excluded from args to prevent a function reference from
    // being spread onto Redux actions (causes DevTools serialization warnings
    // and breaks time-travel debugging). It is wired via resolve instead.
    args: { serialNumber, isPreProdRequest },
    id: serialNumber,
    successActionType: VALIDATE_PAR_FILE_SUCCESS,
    // failureActionType must be provided so reducers clear loading/error
    // state when PAR file validation fails.
    failureActionType: VALIDATE_PAR_FILE_FAILURE,
    // When onError is provided (typically a modal's setState setter), the
    // error is shown inline inside the modal via ModalErrorContent rather
    // than via a global overlay. If onError is omitted the default handler
    // (silent for GET/readOnly / modal for mutation) takes over as normal.
    errorHandlers: onError ? [createModalErrorHandler(onError)] : [],
    // Guard against de-dupe: apiCall.js calls resolve(undefined) when a
    // second call fires while one is already in-flight. Without the guard,
    // callback fires unconditionally and opens the download modal with stale
    // validationData from the previous call.
    // Pass the full result action to callback so callers can inspect the
    // response data (e.g. result.data.hasParErrors) without reading from
    // the Redux store separately.
    resolve: (result) => {
      // De-dup guard: apiCall.js resolves with undefined when a second call
      // fires while the first is still in-flight. Do nothing — the first
      // call's callback will open the modal when it completes.
      if (!result) return
      // Optional callback — only called when provided (onError-only path has no callback).
      if (callback) callback(result)
    },
  })

export const DOWNLOAD_PAR_FILE_SUCCESS = 'DOWNLOAD_PAR_FILE_SUCCESS'
export const DOWNLOAD_PAR_FILE_FAILURE = 'DOWNLOAD_PAR_FILE_FAILURE'
export const downloadParFile = (
  serialNumber,
  isPreProdRequest = false,
  callback,
  onError
) =>
  applicationActions.promiseApiAction({
    asyncFn: vehicles.downloadParFile,
    // isModalTriggered is included in args so it appears on the dispatched
    // DOWNLOAD_PAR_FILE_FAILURE action. The downloadParFile saga checks this
    // flag to suppress the global error notification when the failure is already
    // shown inline inside a modal via createModalErrorHandler(onError).
    args: { serialNumber, isPreProdRequest, isModalTriggered: !!onError },
    id: serialNumber,
    successActionType: DOWNLOAD_PAR_FILE_SUCCESS,
    failureActionType: DOWNLOAD_PAR_FILE_FAILURE,
    errorHandlers: onError ? [createModalErrorHandler(onError)] : [],
    resolve: callback,
    // Do NOT set reject: onError here. When onError is provided, the error is
    // already fully handled by createModalErrorHandler(onError) in the handler
    // chain above — which calls onError(err) and returns HANDLED so no global
    // modal fires. Adding reject: onError would call onError a second time
    // (once via the handler chain, once via apiCall's reject(handlerErr)),
    // duplicating any side effects. This matches validateParFile's pattern.
  })

export const ADD_FEATURE_VALIDATION_ERROR = 'ADD_FEATURE_VALIDATION_ERROR'
export const addFeatureValidationError = () => (dispatch) => {
  dispatch({
    type: ADD_FEATURE_VALIDATION_ERROR,
  })
}

export const VEHICLE_CONTAINS_OBSOLETE_FEATURE =
  'VEHICLE_CONTAINS_OBSOLETE_FEATURE'
